import { FINANCIAL_INSTITUTION_IDS } from "@/constants/financialInstitutions";
import {
  PERMISSION_RULES,
  type PermissionRule,
  type PermissionRuleContext,
} from "@/lib/permissions/permissionRules";
import {
  applyPermissionGrantsPatch,
  createEmptyPermissionGrants,
  type PermissionGrants,
} from "@/lib/permissions/permissionGrantsModel";
import { NAV_ITEMS_LIST } from "@/constants/navigation";
import {
  NAV_ITEM_SEGMENT_TO_USER_FEATURE_TYPE,
  type UserProfileCanAccess,
} from "@/constants/userFeatureType";
import type { UserProfileState } from "@/types/userProfile";
import type { FeaturesProperties } from "@/types/features";
import { isNavSegmentAccessible } from "@/lib/permissions/pathAccess";

const mergePermissionGrantsFromRules = (
  input: {
    organisation: string;
    groups: string[];
  },
  rules: readonly PermissionRule[] = PERMISSION_RULES,
): { permissionGrants: PermissionGrants; isExagens: boolean } => {
  const organisation = input.organisation.trim();
  const isExagens = organisation === FINANCIAL_INSTITUTION_IDS.EXAGENS;
  const ctx: PermissionRuleContext = {
    organisation,
    groups: input.groups,
    isExagens,
  };

  const permissionGrants = rules.reduce<PermissionGrants>(
    (acc, rule) =>
      rule.when(ctx) ? applyPermissionGrantsPatch(acc, rule.patch) : acc,
    createEmptyPermissionGrants(),
  );

  return { permissionGrants, isExagens };
};

export type Permissions = {
  permissionGrants: PermissionGrants;
  canAccess: UserProfileCanAccess;
  isExagens: boolean;
};

export const getPermissions = (
  profile: Pick<UserProfileState, "organisation" | "groups">,
  featuresData: Partial<FeaturesProperties> = {},
): Permissions => {
  const organisationTrimmed =
    typeof profile.organisation === "string" ? profile.organisation.trim() : "";
  const { permissionGrants, isExagens } = mergePermissionGrantsFromRules({
    organisation: organisationTrimmed,
    groups: profile.groups,
  });
  const canAccess = {} as UserProfileCanAccess;
  for (const item of NAV_ITEMS_LIST) {
    const feature = NAV_ITEM_SEGMENT_TO_USER_FEATURE_TYPE[item.segment];
    canAccess[feature] = isNavSegmentAccessible(
      item.segment,
      permissionGrants,
      featuresData,
    );
  }
  return {
    permissionGrants,
    canAccess,
    isExagens,
  };
};
